import If from 'components/If';
import ProcessingButton from 'components/ProcessingButton';
import SimpleModal, { ModalRef } from 'components/SimpleModal';
import {
  FormDateTime,
  FormInput,
  FormObject,
  FormSelect,
  FormState,
  Labeled,
  FloatType,
  FormTextArea,
} from 'component_utils/FormUtils';
import { run } from 'component_utils/utils';
import { IProductionResource, IWorkOrder, IWorkOrderStep } from 'interfaces';
import { IProductionWorkTime } from 'interfaces/entities-production';
import React, { FC, useCallback, useMemo, useState } from 'react';
import { useApi } from 'utils/API';
import { useTranslations } from 'utils/language';
import Translation from 'utils/Language/Translation';
import { addNotification } from 'utils/NotificationManager';
import { first, sortBy } from 'lodash';
import { useSpecializations } from 'utils/SpecializationProvider';
import Item from 'components/objects/item';
import { EventEmitter } from 'events';

export const workedTimeEventEmitter = new EventEmitter()

const Editor: FC<{
  getTemplate: () => IProductionWorkTime;
  modalRef?: React.MutableRefObject<ModalRef>;
  trigger: (open: any) => any;
}> = ({ trigger, getTemplate, modalRef }) => {
  const [state, setState] = useState<IProductionWorkTime>({} as IProductionWorkTime);
  const specializations = useSpecializations()
  const T = useTranslations()
  const api = useApi();

  const [openWorkOrders, setOpenWorkOrders] = useState<IWorkOrder[]>([])
  const [resources, setResources] = useState<IProductionResource[]>([])
  const [workOrderSteps, setWorkOrderSteps] = useState<IWorkOrderStep[]>([])

  const steps = useMemo(() => sortBy(workOrderSteps, it => it.stepNumber), [workOrderSteps])

  const form = (
    <div className={'nested-flex-container'}>
      <Labeled title={<Translation name="T.objects.workConfirmation.workOrderCode" />}>
        <FormObject<IWorkOrder>
          path="workOrderCode"
          autocomplete={useCallback((s) =>
            openWorkOrders
              .filter((it) => it.visualCode.includes(s) || it.producedItem.visualCode.includes(s))
              .map((it) => ({
                label: `${it.visualCode} - ${it.producedItem.visualCode} - ${it.producedItem.name}`,
                value: it.code,
              }))
              .slice(0, 100),
            [openWorkOrders]
          )}
          find={useCallback(
            async (s) => first(await api.production.findWorkOrders([s])), [api]
          )}
          getCode={useCallback((it) => it.code, [])}
          getEditValue={useCallback(it => it.visualCode, [])}
          render={useCallback((it) => (
            <>
              {it.visualCode} - <Item inline obj={it.producedItem}/>
            </>
          ), [])}
          onChange={useCallback(async (oldValue, newValue, foundObject) => {
            // _be_worktime_registration_{"workOrderCode":"565e9a9f-a06a-4a3f-ac49-a6d8300e33ce","workOrderStep":"8e3c843d-5f76-4160-9cd2-bdbdc7db83ee","workOrderWorkCenter":"01111602-9636-4971-a5a1-4871375b41b0","description":"foobar"}
            if (newValue && newValue.startsWith("_be_worktime_registration_")) {
              const json = JSON.parse(newValue.substring("_be_worktime_registration_".length))
              const workOrder = first(await api.production.getWorkOrders([json.workOrderCode]));
              const workOrderSteps = await api.production.getWorkOrderSteps(workOrder.code)
              const workCenter = json.workOrderWorkCenter || workOrderSteps.find(it => it.stepCode === json.workOrderStep).plannedWorkCenterCode
              setState(old => ({
                ...old,
                workOrderCode: json.workOrderCode,
                workOrderStep: json.workOrderStep,
                workCenter: workCenter,
                description: json.description || old.description
              }))
              setWorkOrderSteps(workOrderSteps)
              return
            }

            if (specializations?.augmentations?.production?.timeBooking?.onWorkOrderCodeChange?.(newValue, foundObject, setState, setWorkOrderSteps)) {
              return
            }

            if (newValue && foundObject) {
              setWorkOrderSteps(await api.production.getWorkOrderSteps(newValue))
            } else {
              setWorkOrderSteps([])
            }
            setState(old => ({ ...old, workOrderStep: null }))
          }, [specializations, api])}
        />
      </Labeled>

      <Labeled title={<Translation name="T.objects.workConfirmation.workOrderStep" />}>
        <FormSelect
          path="workOrderStep"
          onChange={(_, newValue) => {
            if (newValue) {
              const step = steps.find((it) => it.stepCode === newValue);
              if (step && step.plannedWorkCenterCode && state.workCenter !== step.plannedWorkCenterCode) {
                setState((old) => ({
                  ...old,
                  workCenter: step.plannedWorkCenterCode,
                }));
              }
            }
          }}
          options={[
            {
              label: '',
              value: null,
            },
            ...steps.map((it) => ({
              label: `${it.stepNumber} - ${it.description}`,
              value: it.stepCode,
            })),
          ]}
        />
      </Labeled>

      <Labeled title={<Translation name="T.objects.workConfirmation.workCenter" />}>
        <FormObject<IProductionResource>
          path="workCenter"
          autocomplete={(s) =>
            resources
              .filter((it) => it.visualCode.toLowerCase().includes(s.toLowerCase()) || it.name.toLowerCase().includes(s.toLowerCase()))
              .map((it) => ({
                label: `${it.visualCode} - ${it.name}`,
                value: it.code,
              }))
          }
          find={(s) => resources.find((it) => it.code === s || it.visualCode === s)}
          getCode={(it) => it.code}
          getEditValue={(it => it.visualCode)}
          render={(it) => (
            <>
              {it.visualCode} - {it.name}
            </>
          )}
        />
      </Labeled>

      <Labeled title={<Translation name="T.objects.workConfirmation.workType" />}>
        <FormSelect
          path='workTimeType'
          options={[
            { label: "", value: "" },
            { label: T("T.objects.workConfirmation.workTypeSetup"), value: "SETUP" },
            { label: T("T.objects.workConfirmation.workTypeWork"), value: "WORK" },
          ]}
        />
      </Labeled>

      <Labeled title={<Translation name="T.objects.workConfirmation.description" />}>
        <FormTextArea path="description" rows={2} />
      </Labeled>

      <If cond={state.workTimeType === 'WORK'}>
        <Labeled title={<Translation name="T.objects.workConfirmation.produced" />}>
          <FormInput path="produced" typed={FloatType} />
        </Labeled>

        <Labeled title={<Translation name="T.objects.workConfirmation.scrap" />}>
          <FormInput path="scrap" typed={FloatType} />
        </Labeled>
      </If>

      <Labeled title={<Translation name="T.objects.workConfirmation.startTime" />}>
        <FormDateTime path="startTime" />
      </Labeled>

      <Labeled title={<Translation name="T.objects.workConfirmation.endTime" />}>
        <FormDateTime path="endTime" />
      </Labeled>

      {specializations?.augmentations?.production?.timeBooking?.customFields && run(() => {
        const C = specializations?.augmentations?.production?.timeBooking.customFields
        return <C state={state} setState={setState}/>
      })}
    </div>
  )

  return (
    <SimpleModal
      fullscreen
      ref={modalRef}
      modalIdentifier='production-worktimes-editor'
      title={<Translation name="T.misc.editWorkedTimeslots" />}
      trigger={trigger}
      onOpen={() => {
        const state = getTemplate()
        setState(state);
        run(async () => {
          if (state.workOrderCode) {
            setWorkOrderSteps(await api.production.getWorkOrderSteps(state.workOrderCode))
          } else {
            setWorkOrderSteps([])
          }
          setResources(await api.production.getResources())
          setOpenWorkOrders(await api.production.getOpenWorkOrders('workConfirmation'))
        })
      }}
    >
      {(close) => (
        <FormState state={state} setState={setState} containerProps={{ className: 'flex-container' }}>
          {form}

          <ProcessingButton
            block
            color={state.endTime ? 'primary' : 'success'}
            onClick={async () => {
              // make sure the configuration is valid
              const wo = first(await api.production.getWorkOrders([state.workOrderCode]))
              if (
                !wo ||
                !workOrderSteps.find((it) => it.stepCode === state.workOrderStep) ||
                !(!state.workCenter || resources.find((it) => it.code === state.workCenter))
              ) {
                throw addNotification('danger', <Translation name="T.errors.invalidInput" />);
              }

              await api.production.saveWorkTimes({
                ...state,
                endTime: state.endTime ? state.endTime : null,
              });
              workedTimeEventEmitter.emit('reload_worked_times')
              close();
            }}
          >
            <Translation name={state.endTime ? 'T.misc.save' : 'T.misc.start'} />
          </ProcessingButton>
        </FormState>
      )}
    </SimpleModal>
  );
};
export default Editor;
